import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useSetPageContext } from "@/hooks/use-page-context";
import {
	FileText,
	Loader2,
	RefreshCw,
	AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { StrategyRevealImmersive } from "@/components/strategy/reveal/StrategyRevealImmersive";
import { StrategyRevealV2 } from "@/components/strategy-v2/StrategyRevealV2";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/hooks/use-session";
import { useStrategy, useStrategyStatus } from "@/hooks/use-strategy";
import { trackStrategyViewed } from "@/lib/analytics-events";
import { apiService } from "@/lib/api-service";
import { adaptStrategyReveal } from "@/lib/strategy-reveal-adapter";

// ---------------------------------------------------------------------------
// Route search params
// ---------------------------------------------------------------------------

interface StrategySearchParams {
	chapter?: string;
}

export const Route = createFileRoute("/_app/_/strategy/")({
	component: StrategyIndexPage,
	validateSearch: (search: Record<string, unknown>): StrategySearchParams => ({
		chapter: typeof search.chapter === "string" ? search.chapter : undefined,
	}),
});

// ---------------------------------------------------------------------------
// Chapter key → index mapping for search param
// ---------------------------------------------------------------------------

const CHAPTER_KEY_MAP: Record<string, number> = {
	landscape: 0,
	voters: 1,
	math: 2,
	position: 3,
	coalition: 4,
	journey: 5,
	feedback: 6,
	tomorrow: 7,
};

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

function StrategyIndexPage() {
	useSetPageContext({
		page_key: "strategy.index",
		page_title: "Campaign Strategy",
		available_actions: ["generate_content"],
		current_data_summary: null,
	});

	const navigate = useNavigate();
	const { chapter: chapterParam } = Route.useSearch();
	const { data: statusData, isLoading: statusLoading } = useStrategyStatus();

	const status = statusData?.status;
	const isReady = status === "ready";

	// Only fetch full strategy when status is ready
	const { data: strategyData, isLoading: strategyLoading } = useStrategy();

	// Resolve the initial chapter from the search param
	const initialChapter = chapterParam
		? (CHAPTER_KEY_MAP[chapterParam] ?? 0)
		: 0;

	// Adapt the raw strategy_reveal JSONB into the typed immersive format
	const strategyReveal = strategyData?.strategy_reveal as
		| Record<string, unknown>
		| undefined;
	const immersiveData = adaptStrategyReveal(strategyReveal ?? null);

	// Detect v2 data: v2 reveal has opening_frame at the top level
	const isV2 = !!(strategyReveal && "opening_frame" in strategyReveal);

	// Mark strategy reveal as seen so the force-redirect gate doesn't fire again.
	// Gate on strategyData loaded (not just isReady) so the flag isn't written
	// while the loading skeleton is still showing — that would break first-visit
	// checks that read the flag synchronously during render.
	const session = useSession();
	const revealContentReady = isReady && !strategyLoading && !!strategyData;
	useEffect(() => {
		if (revealContentReady && session?.campaign?.id) {
			localStorage.setItem(
				`strategy_reveal_seen_${session.campaign.id}`,
				"1",
			);
		}
	}, [revealContentReady, session?.campaign?.id]);

	// Track strategy view (fires once when strategy loads)
	useEffect(() => {
		if (strategyData?.id) {
			trackStrategyViewed(strategyData.id as number);
		}
	}, [strategyData?.id]);

	if (statusLoading) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// Interview not completed yet -- prompt to complete it
	if (status === "interview_pending") {
		return <InterviewPendingScreen navigate={navigate} />;
	}

	// Strategy generation failed -- show error with retry
	if (status === "failed") {
		return <StrategyFailedScreen />;
	}

	// Strategy is ready -- show the immersive reveal
	if (isReady) {
		if (strategyLoading) {
			return (
				<div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
					<div className="w-full max-w-xl space-y-8 text-center">
						<Skeleton className="h-6 w-48 mx-auto" />
						<Skeleton className="h-12 w-80 mx-auto" />
						<div className="flex justify-center gap-12">
							<Skeleton className="h-16 w-24" />
							<Skeleton className="h-16 w-24" />
						</div>
						<Skeleton className="h-4 w-96 mx-auto" />
						<Skeleton className="h-20 w-full" />
					</div>
				</div>
			);
		}

		// V2 pipeline output — render the v2 chapter-based reveal
		if (isV2 && strategyReveal) {
			return <StrategyRevealV2 data={strategyReveal} showCorrections />;
		}

		// V1 pipeline output — render the original immersive reveal
		if (!immersiveData) {
			return (
				<div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
					<div className="max-w-md text-center space-y-4">
						<h2 className="text-2xl font-serif font-medium text-foreground">
							Strategy Not Available
						</h2>
						<p className="text-muted-foreground leading-relaxed">
							Your immersive strategy experience is not yet available.
							This may happen if your strategy hasn&apos;t been
							generated in the new format.
						</p>
						<button
							type="button"
							onClick={() => navigate({ to: "/strategy/dashboard" })}
							className="text-primary hover:underline font-medium"
						>
							View Strategy Dashboard
						</button>
					</div>
				</div>
			);
		}

		return (
			<StrategyRevealImmersive
				data={immersiveData}
				initialChapter={initialChapter}
			/>
		);
	}

	// Not ready -- show simple generating message
	return (
		<div className="flex items-center justify-center min-h-[60vh]">
			<div className="max-w-md w-full space-y-4 text-center">
				<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
					<Loader2 className="h-7 w-7 animate-spin text-blue-600" />
				</div>
				<h2 className="text-xl font-semibold">Strategy generation in progress</h2>
				<p className="text-sm text-muted-foreground">
					We're crafting your personalized campaign strategy. This usually takes a few minutes.
					We'll email you when it's ready.
				</p>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Interview pending screen
// ---------------------------------------------------------------------------

function InterviewPendingScreen({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
	return (
		<div className="flex items-center justify-center min-h-[60vh]">
			<div className="max-w-lg w-full space-y-6 text-center">
				<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
					<FileText className="h-6 w-6 text-amber-600" />
				</div>
				<h2 className="text-xl font-semibold">Complete Your Interview</h2>
				<p className="text-sm text-muted-foreground">
					We need your interview responses before we can generate your
					personalized campaign strategy.
				</p>
				<div className="flex flex-col gap-3 items-center">
					<Button onClick={() => navigate({ to: "/interview/session" })}>
						Start Interview
					</Button>
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Failed screen with retry
// ---------------------------------------------------------------------------

function StrategyFailedScreen() {
	const queryClient = useQueryClient();

	const retryMutation = useMutation({
		mutationFn: () => apiService.retryStrategyGeneration(),
		onSuccess: () => {
			toast.success("Strategy generation restarted.");
			queryClient.invalidateQueries({ queryKey: ["strategy-status"] });
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to retry strategy generation.");
		},
	});

	return (
		<div className="flex items-center justify-center min-h-[60vh]">
			<div className="max-w-lg w-full space-y-6 text-center">
				<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
					<AlertTriangle className="h-6 w-6 text-red-600" />
				</div>
				<h2 className="text-xl font-semibold">Strategy Generation Failed</h2>
				<p className="text-sm text-muted-foreground">
					Something went wrong while generating your campaign strategy.
					This can happen due to temporary service issues. Please try again.
				</p>
				<Button
					onClick={() => retryMutation.mutate()}
					disabled={retryMutation.isPending}
				>
					{retryMutation.isPending ? (
						<Loader2 className="size-4 animate-spin mr-2" />
					) : (
						<RefreshCw className="size-4 mr-2" />
					)}
					Try Again
				</Button>
			</div>
		</div>
	);
}
